import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "../supabaseClient";

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [pendingInvites, setPendingInvites] = useState([]);
  const [dismissedInvites, setDismissedInvites] = useState([]);
  const [seenInvites, setSeenInvites] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const fetchPendingInvites = useCallback(async () => {
    if (!user) {
      setPendingInvites([]);
      return;
    }

    try {
      setLoading(true);
      const { data: invites, error } = await supabase
        .from("organizationInvites")
        .select(
          `
          id,
          organizationID,
          email,
          status,
          createdAt,
          expiredAt,
          roleIDs,
          seenAt,
          invitedBy,
          organization:organizations (
            id,
            name,
            nameAbbreviation,
            description
          )
        `
        )
        .eq("email", user.email.toLowerCase())
        .eq("status", "pending")
        .gt("expiredAt", new Date().toISOString())
        .is("seenAt", null) // Only show invitations that haven't been seen/dismissed
        .order("createdAt", { ascending: false });

      if (error) throw error;

      // If we have invites, get the user details for invitedBy
      if (invites && invites.length > 0) {
        const inviterIds = invites
          .map((invite) => invite.invitedBy)
          .filter(Boolean);

        if (inviterIds.length > 0) {
          // Fetch user details using the edge function
          const { data: inviterUsers, error: userError } =
            await supabase.functions.invoke("get-users-by-ids", {
              body: { user_ids: inviterIds },
            });

          if (!userError && inviterUsers?.data) {
            // Combine the data
            const invitesWithUsers = invites.map((invite) => ({
              ...invite,
              invitedByUser: inviterUsers.data.find(
                (user) => user.id === invite.invitedBy
              ),
            }));
            setPendingInvites(invitesWithUsers);
            return;
          }
        }
      }

      // Fallback: just set the invites without user details
      setPendingInvites(invites || []);
    } catch (err) {
      console.error("Error fetching pending invites:", err);
      setPendingInvites([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchDismissedInvites = useCallback(async () => {
    if (!user) {
      setDismissedInvites([]);
      return;
    }

    try {
      const { data: invites, error } = await supabase
        .from("organizationInvites")
        .select(
          `
          id,
          organizationID,
          email,
          status,
          createdAt,
          expiredAt,
          roleIDs,
          seenAt,
          invitedBy,
          organization:organizations (
            id,
            name,
            nameAbbreviation,
            description
          )
        `
        )
        .eq("email", user.email.toLowerCase())
        .eq("status", "pending")
        .gt("expiredAt", new Date().toISOString())
        .not("seenAt", "is", null) // Show invitations that have been seen/dismissed
        .order("seenAt", { ascending: false });

      if (error) throw error;

      // If we have invites, get the user details for invitedBy
      if (invites && invites.length > 0) {
        const inviterIds = invites
          .map((invite) => invite.invitedBy)
          .filter(Boolean);

        if (inviterIds.length > 0) {
          // Fetch user details using the edge function
          const { data: inviterUsers, error: userError } =
            await supabase.functions.invoke("get-users-by-ids", {
              body: { user_ids: inviterIds },
            });

          if (!userError && inviterUsers?.data) {
            // Combine the data
            const invitesWithUsers = invites.map((invite) => ({
              ...invite,
              invitedByUser: inviterUsers.data.find(
                (user) => user.id === invite.invitedBy
              ),
            }));
            setDismissedInvites(invitesWithUsers);
            return;
          }
        }
      }

      // Fallback: just set the invites without user details
      setDismissedInvites(invites || []);
    } catch (err) {
      console.error("Error fetching dismissed invites:", err);
      setDismissedInvites([]);
    }
  }, [user]);

  const markInviteAsSeen = useCallback(
    async (inviteId) => {
      try {
        const { error } = await supabase
          .from("organizationInvites")
          .update({ seenAt: new Date().toISOString() })
          .eq("id", inviteId);

        if (error) throw error;

        // Remove from pending invites immediately
        setPendingInvites((prev) =>
          prev.filter((invite) => invite.id !== inviteId)
        );

        // Refresh dismissed invites to include the newly dismissed one
        fetchDismissedInvites();
      } catch (err) {
        console.error("Error marking invite as seen:", err);
      }
    },
    [fetchDismissedInvites]
  );

  const markInviteAsViewed = useCallback((inviteId) => {
    // Mark invitation as viewed locally (for toolbar counter)
    setSeenInvites((prev) => new Set([...prev, inviteId]));
  }, []);

  const restoreInvite = useCallback(
    async (inviteId) => {
      try {
        const { error } = await supabase
          .from("organizationInvites")
          .update({ seenAt: null })
          .eq("id", inviteId);

        if (error) throw error;

        // Remove from dismissed invites
        setDismissedInvites((prev) =>
          prev.filter((invite) => invite.id !== inviteId)
        );

        // Remove from seen invites
        setSeenInvites((prev) => {
          const newSet = new Set(prev);
          newSet.delete(inviteId);
          return newSet;
        });

        // Refresh pending invites to include the restored one
        fetchPendingInvites();
      } catch (err) {
        console.error("Error restoring invite:", err);
      }
    },
    [fetchPendingInvites]
  );

  const refreshInvites = useCallback(() => {
    fetchPendingInvites();
    fetchDismissedInvites();
  }, [fetchPendingInvites, fetchDismissedInvites]);

  useEffect(() => {
    fetchPendingInvites();
    fetchDismissedInvites();
  }, [fetchPendingInvites, fetchDismissedInvites]);

  // Calculate pending invitations count (excluding seen ones for toolbar)
  const pendingInvitationsCount = pendingInvites.filter(
    (invite) => !seenInvites.has(invite.id)
  ).length;

  // Always show the invitations link if there are any pending invites (viewed or not)
  const hasAnyPendingInvites = pendingInvites.length > 0;

  const value = {
    pendingInvites,
    dismissedInvites,
    pendingInvitationsCount,
    hasAnyPendingInvites,
    loading,
    refreshInvites,
    markInviteAsSeen,
    markInviteAsViewed,
    restoreInvite,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
